import type React from 'react';

import { useStreamTimeOffset } from '@vlabs/monokit/entities/streams';
import { AnalyticStreamLink } from '@vlabs/shared/components/stream-link/AnalyticStreamLink';
import { buildLocationString } from '@vlapps/point/src/utils/location';
import { useTranslation } from 'react-i18next';

import st from './BaseVideoAnalyticsDetails.module.sass';
import { getFormattedVaEventDateForDetails } from '../../../getFormattedVaEventDate.js';
import type { AnalyticDetailsEvent } from '../types';

interface VideoAnalyticsEventInfoProps {
  event: AnalyticDetailsEvent;
  extraRows?: React.ReactNode;
}

export function VideoAnalyticsEventInfo({
  event,
  extraRows = undefined,
}: VideoAnalyticsEventInfoProps): React.JSX.Element {
  const { t } = useTranslation();
  const { timeInterval, sourceType } = useStreamTimeOffset(
    event.event.stream_id,
    event.event.overview?.time_offset,
  );
  const location = buildLocationString(event.event);
  const date = getFormattedVaEventDateForDetails(event.create_time, event.end_time);

  return (
    <div className={st.Data}>
      <div className={st.RowTitle}>{t('videoAnalytics:creation date')}</div>
      <div
        className={st.RowData}
        data-testid="workspace.analyticEventDetails.createEndTime"
      >
        {date}
      </div>

      <div className={st.RowTitle}>{t('videoAnalytics:source type.label')}</div>
      <div
        className={st.RowData}
        data-testid="workspace.analyticEventDetails.sourceType"
      >
        {sourceType ? t(`videoAnalytics:source type.${String(sourceType)}`) : null}
      </div>

      <div className={st.RowTitle}>{t('videoAnalytics:source')}</div>
      <div
        className={st.RowData}
        data-testid="workspace.analyticEventDetails.sources"
      >
        <AnalyticStreamLink
          hideId
          size="medium"
          streamId={event.event.stream_id}
        />
      </div>

      <div className={st.RowTitle}>{t('videoAnalytics:place')}</div>
      <div
        className={st.RowData}
        data-testid="workspace.analyticEventDetails.location"
      >
        {location}
      </div>

      <div className={st.RowTitle}>{t('videoAnalytics:frame time')}</div>
      <div
        className={st.RowData}
        data-testid="workspace.analyticEventDetails.frameCreationTime"
      >
        {timeInterval}
      </div>

      {extraRows}
    </div>
  );
}
